/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-shadow */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { message } from 'antd';

import assets from '../../../../../assets';
import Header from '../../../../../components/Header/Header';
import { createCommunityPost } from '../../../../../context/communityReducer';
import {
  useCreateTQProfileShoutout,
} from '../../../../../hooks/mutations/tqConnectMutations/user';
import {
  useTQConnectConfig,
} from '../../../../../hooks/queries/tqConnectQueries';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../../shared/hooks';
import {
  IPostReqBody,
  IPostReqBodyPostSettings,
} from '../../../../../types/communityTypes';
import { ROUTES } from '../../../../../types/routes';
import {
  ITQShoutoutCategories,
  ITQShoutoutCategory,
  TQShoutoutCategoryValue,
  TQShoutoutVisibility,
  TQShoutoutVisibilityType,
} from '../../../../../types/tqConnectTypes';
import {
  ICreateTQShoutoutRequest,
} from '../../../../../types/tqConnectTypes/user';
import {
  DEFAULT_CUSTOM_SHOUTOUT_CATEGORY,
} from '../../../../TQConnect/constants';
import BriefDescriptionEditor from './BriefDescriptionEditor';
import ComposeShoutoutContent from './ComposeShoutoutContent';
import ReviewShoutoutContent from './ReviewShoutoutContent';
import SelectShoutoutCategories from './SelectShoutoutCategories';

export type FormMode =
  | 'select-category'
  | 'compose'
  | 'input-description'
  | 'edit-post-caption'
  | 'review-shoutout';

const headerTitleMap: Record<FormMode, string> = {
  'select-category': 'Select a shoutout category',
  compose: 'Compose shoutout',
  'input-description': 'Brief Description',
  'edit-post-caption': 'Post caption',
  'review-shoutout': 'Review & give shoutout',
};

const initialFormValues: ICreateTQShoutoutRequest = {
  to: '',
  description: '',
  category: null,
  coverImage: assets.images.tqConnectShoutoutPreview,
  visibility: TQShoutoutVisibility.PROFILE_ONLY,
  channels: [],
};

const initialPostSettings: IPostReqBodyPostSettings = {
  disableComment: false,
  hideComment: false,
  hideLikes: false,
};

const ShoutoutCreatePage = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState<FormMode>('select-category');
  const [formValues, setFormValues] =
    useState<ICreateTQShoutoutRequest>(initialFormValues);
  const [postSettings, setPostSettings] =
    useState<IPostReqBodyPostSettings>(initialPostSettings);
  const { shoutoutModalState } = useAppSelector((state) => state.tqConnectUser);

  const { givenBy, givenTo, callback } = shoutoutModalState;

  const defaultPostCaption = `Giving a well-deserved shoutout to ${givenTo?.name ?? 'them'}! 👏🏻 Amazing work - keep shining!`;
  const [postCaption, setPostCaption] = useState(defaultPostCaption);

  const {
    userCommunityAccess: { canPostInChannels },
  } = useAppSelector((state) => state.community);

  useEffect(() => {
    if (givenTo?._id && !formValues.to) {
      setFormValues((prev) => ({ ...prev, to: givenTo._id }));
    }
  }, [givenTo?._id, formValues.to]);

  const { createTQProfileShoutout, isPending: isSubmitting } =
    useCreateTQProfileShoutout();

  const { data: tqConnectConfigResult } = useTQConnectConfig();

  const { shoutoutCategoryList, shoutoutCategories } = useMemo(() => {
    const tqConnectConfig = tqConnectConfigResult?.tqConnectConfig;
    const shoutoutCategories = {
      ...(tqConnectConfig?.shoutoutCategories || {}),
      custom: DEFAULT_CUSTOM_SHOUTOUT_CATEGORY,
    } as ITQShoutoutCategories;
    const shoutoutCategoryList: [
      TQShoutoutCategoryValue,
      ITQShoutoutCategory,
    ][] = Object.entries(shoutoutCategories) as [
      TQShoutoutCategoryValue,
      ITQShoutoutCategory,
    ][];

    return {
      shoutoutCategoryList,
      shoutoutCategories,
    };
  }, [tqConnectConfigResult?.tqConnectConfig]);

  const selectedShoutoutCategory = formValues.category
    ? shoutoutCategories?.[formValues.category.id as TQShoutoutCategoryValue]
    : null;

  const handleBack = useCallback(() => {
    navigate(
      ROUTES.USER_PROFILE,
      {
        userId: givenTo?._id || '',
      },
      { reset: true, search: `tab=shoutouts` },
    );
  }, [navigate, givenTo?._id]);

  useEffect(() => {
    // if givenTo is not set, go back to the previous screen
    if (!givenTo?._id) {
      handleBack();
    }
  }, [givenTo?._id, handleBack]);

  const handleGiveShoutout = useCallback(() => {
    if (!formValues.category || !formValues.description?.trim()) return;
    const to = formValues.to || givenTo?._id;
    if (!to) {
      message.warning('Please select a recipient for the shoutout');
      return;
    }
    if (
      formValues.visibility === 'channel-only' &&
      (!formValues.channels || formValues.channels.length === 0)
    ) {
      message.warning('Please select at least one channel for the shoutout');
      return;
    }
    createTQProfileShoutout(
      {
        ...formValues,
        to,
      },
      {
        onSuccess: (shoutoutResponse) => {
          if (
            formValues.visibility === 'channel-only' &&
            formValues.channels &&
            formValues.channels.length > 0
          ) {
            const reqBody: IPostReqBody = {
              channels: formValues.channels ?? [],
              caption: postCaption,
              layoutType: 'carousel',
              postSettings,
              tqConnectShoutout: shoutoutResponse.data.result._id,
            };
            dispatch(
              createCommunityPost(
                reqBody,
                formValues.channels ?? [],
                postCaption,
                [],
                () => {
                  message.success(
                    givenTo
                      ? `Shoutout given to ${givenTo.name}`
                      : 'Shoutout given',
                  );
                  handleBack();
                  callback?.();
                },
              ),
            );
          } else {
            message.success(
              givenTo ? `Shoutout given to ${givenTo.name}` : 'Shoutout given',
            );
            callback?.();
            handleBack();
          }
        },
      },
    );
  }, [
    formValues,
    givenTo,
    createTQProfileShoutout,
    postCaption,
    postSettings,
    dispatch,
    handleBack,
    callback,
  ]);

  const renderedModalContent = useMemo(() => {
    switch (mode) {
      case 'select-category':
        return (
          <SelectShoutoutCategories
            shoutoutCategoryList={shoutoutCategoryList}
            onCategorySelect={(category) => {
              setFormValues((prev) => ({ ...prev, category }));
              setMode('compose');
            }}
          />
        );
      case 'compose':
        return (
          <ComposeShoutoutContent
            formValues={formValues}
            setFormValues={setFormValues}
            setMode={setMode}
            selectedShoutoutCategory={selectedShoutoutCategory ?? null}
            onOpenDescriptionEditor={() => setMode('input-description')}
          />
        );
      case 'review-shoutout':
        return (
          <ReviewShoutoutContent
            formValues={formValues}
            givenBy={givenBy}
            givenTo={givenTo}
            onEdit={() => setMode('compose')}
            onGiveShoutout={handleGiveShoutout}
            isSubmitting={isSubmitting}
            onVisibilityChange={(visibility: TQShoutoutVisibilityType) =>
              setFormValues((prev) => ({ ...prev, visibility }))
            }
            canPostToCommunity={canPostInChannels}
            selectedChannelIds={formValues.channels ?? []}
            onChannelsChange={(channelIds) =>
              setFormValues((prev) => ({ ...prev, channels: channelIds }))
            }
            postSettings={postSettings}
            onPostSettingsChange={setPostSettings}
            postCaption={postCaption}
            defaultPostCaption={defaultPostCaption}
            onOpenPostCaptionEditor={() => setMode('edit-post-caption')}
          />
        );
      default:
        return null;
    }
  }, [
    formValues,
    mode,
    shoutoutCategoryList,
    selectedShoutoutCategory,
    givenBy,
    givenTo,
    isSubmitting,
    handleGiveShoutout,
    canPostInChannels,
    postSettings,
    postCaption,
    defaultPostCaption,
  ]);

  if (mode === 'input-description') {
    return (
      <div className="shoutout-create-page">
        <BriefDescriptionEditor
          initialDescription={formValues.description ?? ''}
          placeholder={
            selectedShoutoutCategory?.placeholder ??
            'Share what they accomplished and how it reflects their journey.'
          }
          onDone={(description) => {
            setFormValues((prev) => ({ ...prev, description }));
            setMode('compose');
          }}
          onClose={() => setMode('compose')}
        />
      </div>
    );
  }

  if (mode === 'edit-post-caption') {
    return (
      <div className="shoutout-create-page">
        <BriefDescriptionEditor
          title="Post caption"
          allowEmpty
          initialDescription={postCaption || defaultPostCaption}
          placeholder={defaultPostCaption}
          onDone={(caption) => {
            setPostCaption(caption.trim() ? caption : defaultPostCaption);
            setMode('review-shoutout');
          }}
          onClose={() => setMode('review-shoutout')}
          showEmojiPicker
        />
      </div>
    );
  }

  return (
    <div className="shoutout-create-page">
      <Header
        title={headerTitleMap[mode]}
        handleBack={handleBack}
        backType="cross"
      />
      {renderedModalContent}
    </div>
  );
};

export default ShoutoutCreatePage;
